import { useEffect, useMemo, useState } from "react"
import axios from "axios"

const initialState = {
  amount: "",
  payment_date: "",
  payment_method_type: "CASH",
  cash_received_amount: "",
  bank_account_id: "",
  description: "",
}

export default function SettlementModal({
  open,
  note,
  onClose,
  onSubmit,
  isSubmitting = false,
}) {
  const [form, setForm] = useState(initialState)
  const [bankAccounts, setBankAccounts] = useState([])
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(false)

  const remainingAmount = useMemo(() => {
    return Number(note?.remaining_amount ?? 0)
  }, [note])

  useEffect(() => {
    if (!open) return

    setForm({
      amount: remainingAmount > 0 ? String(remainingAmount) : "",
      payment_date: "",
      payment_method_type: "CASH",
      cash_received_amount: "",
      bank_account_id: "",
      description: "",
    })
  }, [open, remainingAmount])

  useEffect(() => {
    if (!open || !note?.outlet_id) {
      setBankAccounts([])
      return
    }

    let cancelled = false

    const loadBankAccounts = async () => {
      setIsLoadingBankAccounts(true)

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_INVENTORY_API_URL}/payment-methods`,
          {
            params: {
              outlet_id: note.outlet_id,
              branch_id: note.outlet_id,
            },
          }
        )

        const rows = Array.isArray(response?.data) ? response.data : []
        const accounts = rows.filter((row) => row?.row_type === "bank_account")

        if (!cancelled) {
          setBankAccounts(accounts)
        }
      } catch (_error) {
        if (!cancelled) {
          setBankAccounts([])
        }
      } finally {
        if (!cancelled) {
          setIsLoadingBankAccounts(false)
        }
      }
    }

    loadBankAccounts()

    return () => {
      cancelled = true
    }
  }, [open, note?.outlet_id])

  if (!open || !note) return null

  const formatCurrency = (value) =>
    Number(value ?? 0).toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const amount = Number(form.amount)
    const cashReceivedAmount = Number(form.cash_received_amount || 0)
    const changeAmount = Math.max(0, cashReceivedAmount - amount)

    if (form.payment_method_type === "CASH") {
      if (cashReceivedAmount <= 0) {
        window.alert("Uang customer wajib diisi.")
        return
      }

      if (cashReceivedAmount < amount) {
        window.alert("Uang customer kurang dari nominal bayar.")
        return
      }
    }

    if (form.payment_method_type === "TRANSFER" && !form.bank_account_id) {
      window.alert("Rekening bank tujuan wajib dipilih.")
      return
    }

    await onSubmit({
      amount,
      payment_date: form.payment_date || null,
      payment_method_type: form.payment_method_type || "CASH",
      cash_received_amount: form.payment_method_type === "CASH" ? cashReceivedAmount : null,
      change_amount: form.payment_method_type === "CASH" ? changeAmount : 0,
      bank_account_id: form.payment_method_type === "TRANSFER" ? form.bank_account_id : null,
      description: form.description || null,
    })
  }

  const inputClassName =
    "w-full px-4 py-2.5 text-sm bg-white border rounded-lg border-neutral-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200"
  const amount = Number(form.amount || 0)
  const cashReceivedAmount = Number(form.cash_received_amount || 0)
  const changeAmount = Math.max(0, cashReceivedAmount - amount)
  const isCash = form.payment_method_type === "CASH"
  const isTransfer = form.payment_method_type === "TRANSFER"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                Pelunasan Debit Note
              </h3>
              <p className="text-sm text-neutral-500 mt-0.5">
                Catat pembayaran untuk debit note customer.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-full transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            <div className="grid grid-cols-1 gap-3 p-4 border border-neutral-200 rounded-xl bg-neutral-50">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-500">
                    Note Number
                  </div>
                  <div className="text-sm font-semibold text-neutral-900">
                    {note.note_number || "-"}
                  </div>
                </div>
                <span className="px-2 py-1 text-xs rounded font-medium bg-orange-100 text-orange-700">
                  {note.type || "DEBIT"}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-neutral-500">Original</div>
                  <div className="font-semibold text-neutral-800">
                    Rp {formatCurrency(note.original_amount)}
                  </div>
                </div>
                <div>
                  <div className="text-neutral-500">Sudah Dibayar</div>
                  <div className="font-semibold text-neutral-800">
                    Rp {formatCurrency(note.used_amount)}
                  </div>
                </div>
                <div>
                  <div className="text-neutral-500">Sisa Tagihan</div>
                  <div className="font-semibold text-blue-700">
                    Rp {formatCurrency(remainingAmount)}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                  Nominal Bayar <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">Rp</span>
                  <input
                    type="number"
                    min="0.01"
                    max={remainingAmount || undefined}
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => updateField("amount", e.target.value)}
                    className={`${inputClassName} pl-10`}
                    placeholder="0"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-neutral-500">
                  Maksimal Rp {formatCurrency(remainingAmount)}
                </p>
              </div>

              <div>
                <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                  Tanggal Bayar
                </label>
                <input
                  type="datetime-local"
                  value={form.payment_date}
                  onChange={(e) => updateField("payment_date", e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                  Payment Method Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.payment_method_type}
                  onChange={(e) => {
                    const nextMethod = e.target.value
                    setForm((prev) => ({
                      ...prev,
                      payment_method_type: nextMethod,
                      cash_received_amount: nextMethod === "CASH" ? prev.cash_received_amount : "",
                      bank_account_id: nextMethod === "TRANSFER" ? prev.bank_account_id : "",
                    }))
                  }}
                  className={inputClassName}
                  required
                >
                  <option value="CASH">Cash</option>
                  <option value="TRANSFER">Transfer</option>
                </select>
              </div>

              {isCash && (
                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Uang Customer <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">Rp</span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={form.cash_received_amount}
                      onChange={(e) => updateField("cash_received_amount", e.target.value)}
                      className={`${inputClassName} pl-10`}
                      placeholder="Masukkan uang customer"
                      required
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-neutral-500">Kembalian</span>
                    <span className={`font-semibold ${cashReceivedAmount < amount ? "text-red-600" : "text-emerald-700"}`}>
                      {cashReceivedAmount < amount ? "Kurang" : `Rp ${formatCurrency(changeAmount)}`}
                    </span>
                  </div>
                </div>
              )}

              {isTransfer && (
                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Rekening Bank Tujuan <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.bank_account_id}
                    onChange={(e) => updateField("bank_account_id", e.target.value)}
                    className={inputClassName}
                    disabled={isLoadingBankAccounts}
                    required
                  >
                    <option value="">
                      {isLoadingBankAccounts ? "Memuat rekening..." : "Pilih rekening bank"}
                    </option>
                    {bankAccounts.map((bank) => (
                      <option key={String(bank.id)} value={String(bank.id)}>
                        {bank.bank_name || bank.name || "Bank"} - {bank.account_number || "-"} - {bank.account_name || "-"}
                      </option>
                    ))}
                  </select>
                  {!isLoadingBankAccounts && bankAccounts.length === 0 && (
                    <p className="mt-1 text-xs text-red-600">
                      Rekening bank outlet ini belum tersedia.
                    </p>
                  )}
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                  Keterangan
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  className={`${inputClassName} min-h-[110px] resize-y`}
                  placeholder="Contoh: pelunasan transfer bank / kasir outlet"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2.5 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting || remainingAmount <= 0}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {isSubmitting ? "Menyimpan..." : "Simpan Pelunasan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
